import type { FlowTemplate } from "../types";

/**
 * QP-150 multi-portfolio variant of {@link advisoryMultiStandard}.
 *
 * Reorganized for the portfolio-page model: the goal page keeps only goal-level inputs
 * (name + risk), and the portfolio content is split across three `portfolio` pages —
 * Portfolio (inputs), Analysis (allocation + X-ray), Forecast (forecast + historical return).
 * The runtime renders each portfolio page once per portfolio of each goal.
 *
 * `multiPortfolio` ships `true` — advisors can add multiple portfolios per goal.
 *
 * Public pages mirror the internal structure with two differences:
 * - Trapets Lookup is excluded (advisor-administered PEP/sanctions check)
 * - Sumsub Verification replaces it with liveness-only verification for the client
 */
export const advisoryMultiPortfolioStandard: FlowTemplate = {
  key: "advisory-multi-portfolio-standard",
  category: "advisory-multi",
  language: "en_GB",
  name: "Advisory Multi-Goal, Multi-Portfolio - Standard",
  description:
    "Full multi-goal advisory with a dedicated portfolio page per goal. Portfolio inputs and analysis (X-Ray, forecasts) live on the portfolio page; costs and wealth plan are instance-wide.",
  badges: ["MiFID II", "Multi-Goal", "Multi-Portfolio", "X-Ray"],

  multiGoal: true,
  // Portfolio page works with one portfolio per goal when false. Set true to enable the
  // per-goal add/remove-portfolio tab strip.
  multiPortfolio: true,
  allowPublicStart: false,
  allowPublicPdfGen: false,
  allowPublicSigning: false,

  pages: [
    // === BEFORE GOALS (Global pages) ===
    {
      name: "Classification",
      visibility: "internal",
      pageType: "global",
      globalPosition: "before_goals",
      enableAdvisorNotes: true,
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "client_classification",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Client Classification",
                description:
                  "Answer the questions below to determine your client classification in accordance with MiFID II regulations.",
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "product_assessment",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Product Assessment",
                description:
                  "Tell us about your knowledge and experience with each type of investment. This helps us recommend suitable products for your portfolio.",
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
          ],
        },
        {
          layoutType: "two_column",
          components: [
            {
              type: "trapets_lookup",
              columnPosition: "left",
              config: {
                style: "border",
                title: "PEP/Sanctions Check",
                description:
                  "Verify the client against PEP (politically exposed person) and sanctions databases.",
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_list",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Portfolios",
                description: "Overview of your investment portfolios and their payment schedules.",
                showAmount: true,
                showFrequency: true,
                showDates: true,
              },
            },
          ],
        },
      ],
      components: [
        {
          type: "sumsub_verification",
          config: {
            style: "border",
            title: "Identity Verification",
            description: "Verify the client's identity using document and biometric checks.",
            livenessVerification: false,
          },
        },
      ],
    },
    {
      name: "Financial Situation",
      visibility: "internal",
      pageType: "global",
      globalPosition: "before_goals",
      components: [
        {
          type: "financial_situation",
          config: {
            style: "none",
            title: "Financial Situation",
            description:
              "Please provide information about your income, assets, liabilities, and fixed commitments.",
            showIncome: true,
            showAssets: true,
            showLiabilities: true,
            showCommitments: true,
            showCapacity: true,
            showStabilityQuestion: true,
            showExpectingChangesQuestion: true,
            showCapacityClassification: true,
            showSummaryMetrics: true,
            conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
          },
        },
      ],
    },

    // === GOAL PAGE (goal-level inputs only) ===
    {
      name: "Goal",
      visibility: "internal",
      pageType: "goal",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "goal_input",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Goal Details",
                description: "Name this investment goal.",
              },
            },
            {
              type: "risk_assessment",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Risk Assessment",
                description:
                  "Answer some questions to help us understand your investment horizon and risk tolerance.",
              },
            },
          ],
        },
      ],
    },

    // === PORTFOLIO PAGES (per-portfolio inputs + analytics; each rendered once per portfolio) ===
    {
      name: "Portfolio",
      visibility: "internal",
      pageType: "portfolio",
      components: [
        {
          type: "portfolio_input",
          config: {
            style: "border",
            title: "Portfolio Details",
            description:
              "Set the investment amount, schedule, and portfolio type for this portfolio.",
            showPortfolioType: true,
            allowedPortfolioTypes: ["model", "custom", "dynamic"],
            showDateInputs: true,
          },
        },
        {
          type: "custom_portfolio",
          config: {
            style: "border",
            title: "Custom Portfolio",
            description:
              "Build a custom portfolio by selecting funds and specifying investment amounts.",
            showIsin: true,
            showProductType: true,
          },
        },
      ],
    },
    {
      name: "Analysis",
      visibility: "internal",
      pageType: "portfolio",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "portfolio_display",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Portfolio Allocation",
                description:
                  "Based on your risk profile, here is your recommended portfolio allocation.",
                showPortfolioName: true,
              },
            },
            {
              type: "asset_allocation",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Asset Allocation",
                description: "How your investment is distributed across different asset classes.",
                enableFollowupComparison: true,
                showChart: true,
                showTable: true,
              },
            },
            {
              type: "portfolio_xray",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Equity Style",
                description:
                  "Detailed overview of the portfolio's underlying holdings and exposures.",
                displayMode: "equity_style",
                enableFollowupComparison: true,
                showChart: true,
                showTable: false,
              },
            },
          ],
        },
        {
          layoutType: "tabs_block",
          config: {
            tabs: [
              { id: "tab-1", name: "Asset Allocation" },
              { id: "tab-2", name: "Country Exposure" },
              { id: "tab-3", name: "Regional Exposure" },
              { id: "tab-4", name: "Stock Sector" },
              { id: "tab-5", name: "Bond Sector" },
              { id: "tab-6", name: "Holdings" },
            ],
          },
          components: [
            {
              type: "portfolio_xray",
              tabId: "tab-1",
              config: {
                style: "none",
                description:
                  "Detailed overview of the portfolio's underlying holdings and exposures.",
                displayMode: "asset_allocation",
                enableFollowupComparison: true,
                showTable: true,
                showChart: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-2",
              config: {
                style: "none",
                description:
                  "Detailed overview of the portfolio's underlying holdings and exposures.",
                displayMode: "country_exposure",
                enableFollowupComparison: true,
                showTable: true,
                showChart: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-3",
              config: {
                style: "none",
                description:
                  "Detailed overview of the portfolio's underlying holdings and exposures.",
                displayMode: "regional_exposure",
                enableFollowupComparison: true,
                showTable: true,
                showChart: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-4",
              config: {
                style: "none",
                description:
                  "Detailed overview of the portfolio's underlying holdings and exposures.",
                displayMode: "stock_sector",
                enableFollowupComparison: true,
                showChart: true,
                showTable: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-5",
              config: {
                style: "none",
                description:
                  "Detailed overview of the portfolio's underlying holdings and exposures.",
                displayMode: "bond_sector",
                enableFollowupComparison: true,
                showChart: true,
                showTable: true,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
            {
              type: "portfolio_xray",
              tabId: "tab-6",
              config: {
                style: "none",
                description:
                  "Detailed overview of the portfolio's underlying holdings and exposures.",
                displayMode: "top_holdings",
                showChart: true,
                showTable: false,
                conditionalDisplay: { enabled: false, sourceFieldId: "", operator: "equals" },
              },
            },
          ],
        },
      ],
    },
    {
      name: "Forecast",
      visibility: "internal",
      pageType: "portfolio",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "portfolio_forecast",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Portfolio Forecast",
                description:
                  "Expected portfolio value over time with 95% confidence interval based on historical market data.",
                showStatistics: true,
                showTable: true,
              },
            },
            {
              type: "historical_return",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Historical Return",
                description:
                  "Historical development of the portfolio based on past market data. Historical returns are no guarantee of future returns.",
              },
            },
          ],
        },
      ],
      components: [
        {
          type: "cost_benefit",
          config: {
            style: "border",
            title: "Cost Benefit",
            description:
              "How the proposed portfolio compares with your existing one on expected return, risk and costs, and what switching costs one time.",
          },
        },
      ],
    },

    // === AFTER GOALS (Global pages) ===
    {
      name: "Wealth Plan",
      visibility: "internal",
      pageType: "global",
      globalPosition: "after_goals",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "wealth_plan",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Wealth Plan",
                description:
                  "Plan future withdrawals from your investment portfolio to reach your financial goals.",
              },
            },
            {
              type: "expected_value",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Expected Value",
                description:
                  "Expected portfolio value over time based on expected returns and your investment plan.",
                showChart: true,
                showSummaryTable: true,
                showYearlyTable: true,
              },
            },
          ],
        },
      ],
    },
    {
      name: "Costs",
      visibility: "internal",
      pageType: "global",
      globalPosition: "after_goals",
      layouts: [
        {
          layoutType: "two_column",
          components: [
            {
              type: "cost_chart",
              columnPosition: "left",
              config: {
                style: "border",
                title: "Cost Impact",
                description: "Visualization of how costs affect portfolio value over time.",
                displayMode: "rolling",
              },
            },
            {
              type: "cost_table",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Cost Table",
                description: "Overview of all costs associated with your investment.",
              },
            },
            {
              type: "portfolio_costs",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Advisory Costs",
                description: "Platform and advisory costs for your investment portfolio.",
              },
            },
            {
              type: "product_costs",
              columnPosition: "right",
              config: {
                style: "border",
                title: "Product Costs",
                description:
                  "Ongoing costs for each product in your portfolio, including management fees and transaction costs.",
              },
            },
          ],
        },
      ],
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // PUBLIC PAGES — client-facing mirror of the internal pages.
    // Differences: no trapets_lookup; sumsub_verification with liveness only.
    // ═══════════════════════════════════════════════════════════════════════════

    // === PUBLIC BEFORE GOALS (Global pages) ===
    {
      name: "Classification",
      visibility: "public",
      pageType: "global",
      globalPosition: "before_goals",
      components: [
        {
          type: "client_classification",
          config: {
            style: "border",
            title: "Client Classification",
            description:
              "Answer the questions below to determine your client classification in accordance with MiFID II regulations.",
          },
        },
        {
          type: "product_assessment",
          config: {
            style: "border",
            title: "Product Assessment",
            description:
              "Tell us about your knowledge and experience with each type of investment. This helps us recommend suitable products for your portfolio.",
          },
        },
        {
          type: "sumsub_verification",
          config: {
            style: "border",
            title: "Identity Verification",
            description:
              "Complete identity verification by providing a valid ID document and a selfie.",
            livenessVerification: true,
          },
        },
        {
          type: "portfolio_list",
          config: {
            style: "border",
            title: "Portfolios",
            description: "Overview of your investment portfolios and their payment schedules.",
            showAmount: true,
            showFrequency: true,
            showDates: true,
          },
        },
      ],
    },
    // === PUBLIC GOAL PAGE ===
    {
      name: "Goal",
      visibility: "public",
      pageType: "goal",
      components: [
        {
          type: "goal_input",
          config: {
            style: "border",
            title: "Goal Details",
            description: "Name this investment goal.",
          },
        },
        {
          type: "risk_assessment",
          config: {
            style: "border",
            title: "Risk Assessment",
            description:
              "Answer some questions to help us understand your investment horizon and risk tolerance.",
          },
        },
        {
          type: "portfolio_list",
          config: {
            style: "border",
            title: "Portfolios",
            description: "Overview of your investment portfolios and their payment schedules.",
            showAmount: true,
            showFrequency: true,
            showDates: true,
          },
        },
      ],
    },

    // === PUBLIC PORTFOLIO PAGES ===
    {
      name: "Portfolio",
      visibility: "public",
      pageType: "portfolio",
      components: [
        {
          type: "portfolio_input",
          config: {
            style: "border",
            title: "Portfolio Details",
            description:
              "Set the investment amount, schedule, and portfolio type for this portfolio.",
            showPortfolioType: true,
            allowedPortfolioTypes: ["model", "custom", "dynamic"],
            showDateInputs: true,
          },
        },
        {
          type: "custom_portfolio",
          config: {
            style: "border",
            title: "Custom Portfolio",
            description:
              "Build a custom portfolio by selecting funds and specifying investment amounts.",
            showIsin: true,
            showProductType: true,
          },
        },
      ],
    },
    {
      name: "Analysis",
      visibility: "public",
      pageType: "portfolio",
      components: [
        {
          type: "portfolio_display",
          config: {
            style: "border",
            title: "Portfolio Allocation",
            description:
              "Based on your risk profile, here is your recommended portfolio allocation.",
            showPortfolioName: true,
          },
        },
        {
          type: "asset_allocation",
          config: {
            style: "border",
            title: "Asset Allocation",
            description: "How your investment is distributed across different asset classes.",
            enableFollowupComparison: true,
            showChart: true,
            showTable: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Equity Style",
            description: "Detailed overview of the portfolio's underlying holdings and exposures.",
            displayMode: "equity_style",
            enableFollowupComparison: true,
            showChart: true,
            showTable: false,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "X-Ray Asset Allocation",
            description: "Detailed overview of the portfolio's underlying holdings and exposures.",
            displayMode: "asset_allocation",
            enableFollowupComparison: true,
            showTable: true,
            showChart: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Country Exposure",
            description: "Detailed overview of the portfolio's underlying holdings and exposures.",
            displayMode: "country_exposure",
            enableFollowupComparison: true,
            showTable: true,
            showChart: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Regional Exposure",
            description: "Detailed overview of the portfolio's underlying holdings and exposures.",
            displayMode: "regional_exposure",
            enableFollowupComparison: true,
            showTable: true,
            showChart: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Stock Sector",
            description: "Detailed overview of the portfolio's underlying holdings and exposures.",
            displayMode: "stock_sector",
            enableFollowupComparison: true,
            showChart: true,
            showTable: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Bond Sector",
            description: "Detailed overview of the portfolio's underlying holdings and exposures.",
            displayMode: "bond_sector",
            enableFollowupComparison: true,
            showChart: true,
            showTable: true,
          },
        },
        {
          type: "portfolio_xray",
          config: {
            style: "border",
            title: "Top Holdings",
            description: "Detailed overview of the portfolio's underlying holdings and exposures.",
            displayMode: "top_holdings",
            showChart: true,
            showTable: false,
          },
        },
      ],
    },
    {
      name: "Forecast",
      visibility: "public",
      pageType: "portfolio",
      components: [
        {
          type: "portfolio_forecast",
          config: {
            style: "border",
            title: "Portfolio Forecast",
            description:
              "Expected portfolio value over time with 95% confidence interval based on historical market data.",
            showStatistics: true,
            showTable: true,
          },
        },
        {
          type: "historical_return",
          config: {
            style: "border",
            title: "Historical Return",
            description:
              "Historical development of the portfolio based on past market data. Historical returns are no guarantee of future returns.",
          },
        },
      ],
    },

    // === PUBLIC AFTER GOALS (Global pages) ===
    {
      name: "Wealth Plan",
      visibility: "public",
      pageType: "global",
      globalPosition: "after_goals",
      components: [
        {
          type: "wealth_plan",
          config: {
            style: "border",
            title: "Wealth Plan",
            description:
              "Plan future withdrawals from your investment portfolio to reach your financial goals.",
          },
        },
        {
          type: "expected_value",
          config: {
            style: "border",
            title: "Expected Value",
            description:
              "Expected portfolio value over time based on expected returns and your investment plan.",
            showChart: true,
            showSummaryTable: true,
            showYearlyTable: true,
          },
        },
      ],
    },
    {
      name: "Costs",
      visibility: "public",
      pageType: "global",
      globalPosition: "after_goals",
      components: [
        {
          type: "cost_chart",
          config: {
            style: "border",
            title: "Cost Impact",
            description: "Visualization of how costs affect portfolio value over time.",
            displayMode: "rolling",
          },
        },
        {
          type: "cost_table",
          config: {
            style: "border",
            title: "Cost Table",
            description: "Overview of all costs associated with your investment.",
          },
        },
        {
          type: "portfolio_costs",
          config: {
            style: "border",
            title: "Advisory Costs",
            description: "Platform and advisory costs for your investment portfolio.",
          },
        },
        {
          type: "product_costs",
          config: {
            style: "border",
            title: "Product Costs",
            description:
              "Ongoing costs for each product in your portfolio, including management fees and transaction costs.",
          },
        },
      ],
    },
  ],
};
